import UserMobile from "@/styles/UserMobile";
import { AreaEnum, User, UserPermission } from "@/types/user";
import {
  Button,
  Divider,
  Form,
  FormProps,
  Input,
  Modal,
  notification,
} from "antd";
import { FC, ReactNode, useState } from "react";
import useUserUpdate from "../pages/UserManager/hooks/useUserUpdate";
import { isSame } from "@/utils/checkTool";
import { userUpdatePassword } from "@/apis/user";
import AvatarUpload from "@/styles/AvatarUpload";
import { useTranslation } from 'react-i18next';
import profileLogout from "@/pages/Profile/utils/logout";
import { setStorage, getStorage } from "@/utils/storage";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/store";
import { setCurUser } from "@/store/modules/userReducer";

interface IUserDetail {
  openStatus: boolean;
  onCancel: (user?: User) => void;
  userInfo: User;
}

type UserBasicUpdateFieldType = {
  name1?: string;
  name2?: string;
  mobile?: string;
  areaCode?: AreaEnum;
  avatar?: string;
};

type UserPwdUpdateFieldType = {
  password?: string;
  password2: string;
};

export const UserDetailEdit: FC<IUserDetail> = ({
  openStatus,
  onCancel,
  userInfo,
}) => {
  const { t } = useTranslation();
  const [passwordForm] = Form.useForm();
  const stateDispatch: AppDispatch = useDispatch();
  const [user, setUser] = useState<User>(userInfo);
  const [name1, setName1] = useState<string>(user.name1);
  const [name2, setName2] = useState<string>(user.name2);
  const [areaCode, setAreaCode] = useState<AreaEnum>(user.areaCode);
  const [mobile, setMobile] = useState<string>(user.mobile);
  const [avatar, setAvatar] = useState<string>(user.avatar);
  const [isUpdateBasic, setIsUpdateBasic] = useState<boolean>(false);
  const [upPwdLoading, setUpPwdLoading] = useState<boolean>(false);
  const [loading, { userUpdate, userUpdateMobile }] = useUserUpdate();
  const [notificationApi, contextHolder] = notification.useNotification({ placement: 'top' });

  const handleUpdateBasic = () => {
    if (!isSame(user, { ...user, name1, name2, avatar }, ['name1', 'name2', 'avatar'])) {
      userUpdate(user.id, { name1, name2, avatar, areaCode, mobile }).then(() => {
        setUser({...user, name1, name2, avatar})
        notificationApi.success({ message: t('member.bascInfoUp') })
        setIsUpdateBasic(true)
        // 更新storage、redux curUser信息
        if(user.id === getStorage('curUser').id) {
          setStorage('curUser', { ...user, name1, name2, avatar })
          stateDispatch(setCurUser({ ...user, name1, name2, avatar }))
        }
      })
    }
    if(mobile) {
      if(user.mobile !== mobile || user.areaCode !== areaCode) {
        userUpdateMobile(user.id, { areaCode, mobile }).then(() => {
          setIsUpdateBasic(true)
          setUser({...user, areaCode, mobile})
          exitLogin(t('member.mobileChangeSuc'));
        })
      }
    } else {
      notificationApi.error({ message: t('member.mobileEmpty') })
    }
  }

  const exitLogin = (msg: ReactNode) => {
    if(user.id === getStorage('curUser').id) {
      notification.success({ placement: 'top', message: msg, description: t('member.aginLogin') });
      setTimeout(() => {
        profileLogout()
      }, 3000);
    } else {
      notificationApi.success({ message: msg })
    }
  }

  const handleUpdatePwd = () => {
    setUpPwdLoading(true);
    const pwd = passwordForm.getFieldValue('password')
    userUpdatePassword(user.id, pwd)
      .then(() => {
        exitLogin(t('member.pwdChanged'));
        setTimeout(() => {
          passwordForm.setFieldsValue('password');
          passwordForm.setFieldsValue('password2');
        }, 500);
      })
      .finally(() => setUpPwdLoading(false))
  }

  const handleUpdateError:FormProps["onFinishFailed"] = (errorInfo) => {
    if (errorInfo.errorFields.length > 0) {
      return notificationApi.error({
        description: errorInfo.errorFields[0].errors[0],
        message: t('member.validaFailed'),
      });
    }
  }

  const handleCancel = () => {
    if (isUpdateBasic) {
      let userPermission:UserPermission[] = JSON.parse(JSON.stringify(user.permissions))
      onCancel({ ...user, name1, name2, avatar, areaCode, mobile, permissions: userPermission });
    } else {
      onCancel()
    }
  };

  const basicEditELe = (
    <Form  onFinish={handleUpdateBasic} onFinishFailed={handleUpdateError} style={{ marginTop: 16 }} name="user" autoComplete="off" initialValues={{ ...user }}>
    <Form.Item<UserBasicUpdateFieldType> label={t('member.avatar')} name="avatar">
      <AvatarUpload tooltip={false} uniChar={user.id} avatarId={avatar} onChange={setAvatar} />
    </Form.Item>
    <Form.Item<UserBasicUpdateFieldType> label="Name1" name="name1" rules={[ { max: 100, message: t('member.maxLengthTip') } ]}>
      <Input value={name1} onChange={(e) => setName1(e.target.value)} count={{ show:true, max: 100 }} placeholder={t('login.enterAcnt')} />
    </Form.Item>

    <Form.Item<UserBasicUpdateFieldType> label="Name2" name="name2" rules={[ { max: 100, message: t('member.maxLengthTip') } ]}>
      <Input value={name2} onChange={(e) => setName2(e.target.value)} count={{ show:true, max: 100 }} placeholder={t('login.enterAcnt')} />
    </Form.Item>
    <Form.Item<UserBasicUpdateFieldType> label={t('member.phNumber')} name="mobile" required>
      <UserMobile
        areaCode={areaCode}
        setAreaCode={setAreaCode}
        mobile={mobile}
        setMobile={setMobile}
      />
    </Form.Item>

    <Form.Item>
      <Button style={{ float: 'right' }}  loading={loading} type="primary" htmlType="submit">
        {t('member.sure')}
      </Button>
    </Form.Item>
  </Form>
  )

  const passwordEle = (
    <Form style={{ marginTop: 16 }} onFinishFailed={handleUpdateError} onFinish={handleUpdatePwd} form={passwordForm} name="password" autoComplete="off" initialValues={{ ...user }}>
      <Form.Item<UserPwdUpdateFieldType> label={t('member.pwd')} name='password' required rules={[{ required: true, message: t('member.enterPwd') }, { min: 6, message: t('member.pwdMinLength') }]}>
        <Input.Password placeholder={t('member.enterPwd')} />
      </Form.Item>
      <Form.Item<UserPwdUpdateFieldType> label={t('member.confirmPwd')} name='password2' required rules={[
        { required: true, message: t('member.plConfirmPwd') },
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue('password') === value) {
              return Promise.resolve();
            }
            return Promise.reject(new Error(t('member.pwdDiff')));
          },
        }),
      ]}>
        <Input.Password placeholder={t('member.reenterPwd')} />
      </Form.Item>
      <Form.Item>
        <Button style={{ float: 'right' }} loading={upPwdLoading} type="primary" htmlType="submit">
          {t('member.sure')}
        </Button>
      </Form.Item>
    </Form>
  )

  return (
    <Modal
      title={t('member.memberDetails')}
      open={openStatus}
      confirmLoading={loading}
      onCancel={handleCancel}
      footer={null}
      // destroyOnClose={true}
      // forceRender
      maskClosable={false}
    >
      {contextHolder}
      <div className="modal-item--header">{t('member.basicInfo')}</div>
      {basicEditELe}
      <Divider />
      <div className="modal-item--header">{t('member.pwd')}</div>
      {passwordEle}
    </Modal>
  );
};
